import { getPageNumbers } from '@/lib/pagination';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import { ClubMembershipRequestStatus } from '@shared/enums/domain.enum';
import type { ClubMembershipRequest } from '@shared/types/entities';
import { Check, ChevronLeft, ChevronRight, Mail, X } from 'lucide-react';

import { RequestStatusBadge } from './member-badges';

interface MembershipRequestsTabProps {
  requests: ClubMembershipRequest[];
  reqLoading: boolean;
  reqFetching: boolean;
  reqStatusFilter: ClubMembershipRequestStatus | '';
  onReqStatusFilterChange: (value: string) => void;
  totalReqs: number;
  fromR: number;
  toR: number;
  reqPage: number;
  totalReqPages: number;
  onPageChange: (page: string) => void;
  onProcess: (request: ClubMembershipRequest, action: 'accept' | 'reject') => void;
}

export function MembershipRequestsTab({
  requests,
  reqLoading,
  reqFetching,
  reqStatusFilter,
  onReqStatusFilterChange,
  totalReqs,
  fromR,
  toR,
  reqPage,
  totalReqPages,
  onPageChange,
  onProcess,
}: MembershipRequestsTabProps) {
  return (
    <>
      <div className='mb-6 flex items-center gap-4'>
        <select
          value={reqStatusFilter}
          onChange={e => onReqStatusFilterChange(e.target.value)}
          className='rounded-lg border border-slate-200 px-4 py-2.5 focus:border-emerald-500 focus:outline-none'
        >
          <option value=''>Tous les statuts</option>
          <option value={ClubMembershipRequestStatus.PENDING}>En attente</option>
          <option value={ClubMembershipRequestStatus.ACCEPTED}>Accept&eacute;es</option>
          <option value={ClubMembershipRequestStatus.REJECTED}>Refus&eacute;es</option>
        </select>
        {reqFetching && !reqLoading && <span className='text-xs text-slate-400'>Actualisation&hellip;</span>}
      </div>

      <div className='rounded-xl border border-slate-200 bg-white'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='border-b border-slate-200 bg-slate-50'>
              <tr>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Joueur
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Contact
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Licence
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Message
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Statut
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Date
                </th>
                <th
                  scope='col'
                  className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {reqLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className='px-6 py-4'>
                        <div className='h-4 animate-pulse rounded bg-slate-200' />
                      </td>
                    ))}
                  </tr>
                ))
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={7} className='px-6 py-12 text-center text-sm text-slate-500'>
                    Aucune demande
                  </td>
                </tr>
              ) : (
                requests.map(req => (
                  <tr key={req.id} className='transition hover:bg-slate-50'>
                    <td className='px-6 py-4'>
                      <div className='flex items-center gap-3'>
                        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xs font-bold text-white'>
                          {getInitials(req.user?.firstName ?? null, req.user?.lastName ?? null)}
                        </div>
                        <div>
                          <p className='text-sm font-semibold text-slate-900'>
                            {req.user?.firstName} {req.user?.lastName}
                          </p>
                          <p className='text-xs text-slate-500'>@{req.user?.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <span className='flex items-center gap-1.5 text-sm text-slate-700'>
                        <Mail className='h-3.5 w-3.5 text-slate-400' />
                        {req.user?.email}
                      </span>
                    </td>
                    <td className='px-6 py-4'>
                      <span className='text-sm text-slate-700'>{req.user?.licenseNumber ?? ''}</span>
                    </td>
                    <td className='max-w-xs px-6 py-4'>
                      {req.message ? (
                        <p className='truncate text-sm italic text-slate-600' title={req.message}>
                          &laquo;&nbsp;{req.message}&nbsp;&raquo;
                        </p>
                      ) : (
                        <span className='text-sm text-slate-400'>&mdash;</span>
                      )}
                    </td>
                    <td className='px-6 py-4'>
                      <RequestStatusBadge status={req.status} />
                      {req.status === ClubMembershipRequestStatus.REJECTED && req.rejectedReason && (
                        <p className='mt-1 max-w-32 truncate text-xs italic text-slate-400' title={req.rejectedReason}>
                          {req.rejectedReason}
                        </p>
                      )}
                    </td>
                    <td className='px-6 py-4'>
                      <span className='text-sm text-slate-700'>{formatDateAbbreviatedFr(req.createdAt)}</span>
                    </td>
                    <td className='px-6 py-4'>
                      {req.status === ClubMembershipRequestStatus.PENDING && (
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => onProcess(req, 'accept')}
                            className='flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700'
                          >
                            <Check className='h-3.5 w-3.5' />
                            Accepter
                          </button>
                          <button
                            onClick={() => onProcess(req, 'reject')}
                            className='flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50'
                          >
                            <X className='h-3.5 w-3.5' />
                            Refuser
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination demandes */}
        <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
          <p className='text-sm text-slate-500'>
            {totalReqs === 0 ? 'Aucun résultat' : `${fromR}-${toR} sur ${totalReqs.toLocaleString('fr-FR')} demandes`}
          </p>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => onPageChange(String(Math.max(1, reqPage - 1)))}
              disabled={reqPage === 1}
              className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
              aria-label='Page précédente'
            >
              <ChevronLeft className='h-4 w-4' />
            </button>
            {getPageNumbers(reqPage, totalReqPages).map((p, i) =>
              p === '...' ? (
                <span key={`d-${i}`} className='px-2 text-slate-400'>
                  &hellip;
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(String(p))}
                  aria-current={p === reqPage ? 'page' : undefined}
                  className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                    p === reqPage ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => onPageChange(String(Math.min(totalReqPages, reqPage + 1)))}
              disabled={reqPage >= totalReqPages}
              className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
              aria-label='Page suivante'
            >
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
